import { isStorageKind } from './destination-kinds';
import type { PaigeDestination } from './paige.global';

/** The row's state: what failed for good, then what still waits, else the last delivery. */
export function stateLine(row: PaigeDestination): string {
  const storage = isStorageKind(row.kind) ? storageLine(row) : null;
  if (storage) return storage;
  if (row.health === 'down') return row.lastError ?? 'Deliveries are failing';
  const problems = problemLine(row);
  return problems ?? `${settledLine(row)}${skippedNote(row)}`;
}

/** "N failed: …; N waiting", or null when nothing failed or waits. */
function problemLine(row: PaigeDestination): string | null {
  const failed =
    row.failed > 0 ? `${row.failed} failed${row.lastFailure ? `: ${row.lastFailure}` : ''}` : null;
  // A failure that will be tried again waits; its words are the destination's last error.
  const said = row.failed === 0 && row.lastError ? `: ${row.lastError}` : '';
  const waiting = row.retrying > 0 ? `${row.retrying} waiting${said}` : null;
  const parts = [failed, waiting].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join('; ') : null;
}

function settledLine(row: PaigeDestination): string {
  // A delivery that went through can still say what it did not take (metadata with no place).
  if (row.lastDeliveryAt)
    return `Last delivery ${new Date(row.lastDeliveryAt).toLocaleString()}${row.lastError ? `; ${row.lastError}` : ''}`;
  return row.kind === 'download' ? 'Every confirmed document' : 'Nothing sent yet';
}

/** Documents of a type the destination maps to nothing: said, never counted as a problem. They go when the type map is saved with a place for them, or by Retry. */
function skippedNote(row: PaigeDestination): string {
  if (row.skipped <= 0) return '';
  return `; ${row.skipped} not sent (type not mapped; sent once it is mapped)`;
}

/** What a storage destination still needs before it can deliver; null once it is connected and has a folder. */
function storageLine(row: PaigeDestination): string | null {
  const { connection, location } = row.settings;
  if (!connection) return 'Not connected yet; open it to connect';
  if (connection.state === 'reconnect')
    return 'The account stopped letting Paige in; open it to connect again';
  return location ? null : `Connected as ${connection.account ?? 'the account'}; choose a folder`;
}
